import { useState } from 'react';
import { AxiosError } from 'axios';
import { Plus, Trash2 } from 'lucide-react';
import { Card, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { StatusBadge } from '@/components/ui/Badge';
import { Alert } from '@/components/ui/Alert';
import { useAuthStore } from '@/stores/auth';
import { useRemoveClientStaff, type ClientRecord, type ClientStaff } from '@/api/clients';
import { fmtDob } from '../clientHelpers';
import { AssignStaffDialog } from '../AssignStaffDialog';

/*
 * Staff tab — the supervising BCBA lives on `client.bcba_id` and is
 * shown in a dedicated header above the table; the table below covers
 * additional assignees (RBTs, coverage staff, lead BCBAs, etc). The
 * BCBA is filtered out of the table defensively in case seed data ever
 * puts them in both places.
 *
 * Add / remove are the only mutations — there is no edit flow. Changing
 * a staff member's role on a client is cheap enough to model as
 * remove-then-add, and it keeps the permission FK (`userId`) immutable
 * per entry.
 *
 * Remove uses a two-click confirmation on the row itself rather than a
 * dedicated dialog — the row shows all the context a reviewer needs.
 */
export function ClientStaffTab({ client: c }: { client: ClientRecord }) {
    const canEdit = useAuthStore((s) => s.hasPermission('edit_clients'));
    const removeMut = useRemoveClientStaff(c.id);
    const [assignOpen, setAssignOpen] = useState(false);
    const [confirmingId, setConfirmingId] = useState<string | null>(null);

    const additional = (c.staff || []).filter((s) => !c.bcbaId || s.userId !== c.bcbaId);

    async function onRemoveClick(entry: ClientStaff) {
        if (confirmingId !== entry.id) {
            setConfirmingId(entry.id);
            return;
        }
        try {
            await removeMut.mutateAsync(entry.id);
            setConfirmingId(null);
        } catch {
            /* surfaced below */
        }
    }

    const serverError = (() => {
        const e = removeMut.error;
        if (!e) return null;
        if (e instanceof AxiosError) return e.response?.data?.error ?? 'Failed to remove staff';
        return 'Unexpected error';
    })();

    return (
        <>
            <div className="space-y-4">
                <Card>
                    <CardHeader><CardTitle>Supervising BCBA</CardTitle></CardHeader>
                    {c.bcba ? (
                        <p className="text-sm text-text">{c.bcba}</p>
                    ) : (
                        <p className="text-sm text-text-subtle">No BCBA assigned yet.</p>
                    )}
                </Card>

                <Card className="p-0 overflow-hidden">
                    <CardHeader className="px-5 pt-5 flex items-start justify-between">
                        <CardTitle>Additional Assigned Staff</CardTitle>
                        {canEdit && (
                            <Button
                                type="button"
                                size="sm"
                                variant="primary"
                                onClick={() => setAssignOpen(true)}
                            >
                                <Plus className="w-3.5 h-3.5" /> Assign Staff
                            </Button>
                        )}
                    </CardHeader>

                    {serverError && (
                        <Alert variant="danger" className="mx-5 mb-3">{serverError}</Alert>
                    )}

                    {additional.length === 0 ? (
                        <p className="px-5 pb-5 text-sm text-text-subtle">
                            No additional staff assigned.
                        </p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                        <th className="px-5 py-2 font-bold">Name</th>
                                        <th className="px-3 py-2 font-bold">Role</th>
                                        <th className="px-3 py-2 font-bold">Start</th>
                                        <th className="px-3 py-2 font-bold">Status</th>
                                        {canEdit && <th className="px-5 py-2 font-bold w-32 text-right"></th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {additional.map((s) => {
                                        const confirming = confirmingId === s.id;
                                        const pending = removeMut.isPending && confirming;
                                        return (
                                            <tr key={s.id} className="border-b border-border last:border-b-0">
                                                <td className="px-5 py-2 text-text">{s.name || '—'}</td>
                                                <td className="px-3 py-2 text-text-muted">{s.role || '—'}</td>
                                                <td className="px-3 py-2 text-text-muted whitespace-nowrap">
                                                    {s.start ? fmtDob(s.start) : '—'}
                                                </td>
                                                <td className="px-3 py-2"><StatusBadge status={s.status} /></td>
                                                {canEdit && (
                                                    <td className="px-5 py-2 text-right">
                                                        <Button
                                                            type="button"
                                                            size="sm"
                                                            variant={confirming ? 'danger' : 'ghost'}
                                                            onClick={() => onRemoveClick(s)}
                                                            disabled={pending}
                                                            aria-label={`Remove ${s.name || 'staff member'}`}
                                                        >
                                                            <Trash2 className="w-3.5 h-3.5" />
                                                            {confirming
                                                                ? (pending ? 'Removing…' : 'Confirm')
                                                                : 'Remove'}
                                                        </Button>
                                                    </td>
                                                )}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card>
            </div>

            {canEdit && (
                <AssignStaffDialog
                    client={c}
                    open={assignOpen}
                    onOpenChange={setAssignOpen}
                />
            )}
        </>
    );
}
